<script setup lang="ts">
import DOMPurify from 'dompurify'

import { computed, ref, watch } from 'vue'

import MermaidBlock from './mermaid-block.vue'

import { useMarkdown } from '../../composables/markdown'

interface Props {
  content: string
  class?: string | string[]
}

const props = defineProps<Props>()

// Split the content into ordered segments so ```mermaid fences become live
// diagrams while everything else stays normal markdown. A fenced mermaid block
// only matches once its closing ``` has arrived, so a still-streaming block
// renders as ordinary text/code until complete — no half-parsed diagrams.
type Segment = { type: 'markdown', text: string } | { type: 'mermaid', code: string }

const MERMAID_FENCE = /```mermaid[ \t]*\r?\n([\s\S]*?)```/g

const segments = computed<Segment[]>(() => {
  const src = props.content || ''
  const out: Segment[] = []
  let last = 0
  for (const match of src.matchAll(MERMAID_FENCE)) {
    const start = match.index ?? 0
    if (start > last)
      out.push({ type: 'markdown', text: src.slice(last, start) })
    out.push({ type: 'mermaid', code: match[1] })
    last = start + match[0].length
  }
  if (last < src.length)
    out.push({ type: 'markdown', text: src.slice(last) })
  return out
})

// Rendered HTML per markdown segment, keyed by segment index. Shiki gives code
// blocks their per-language coloring; KaTeX handles math.
const rendered = ref<Record<number, string>>({})
const { process, processSync } = useMarkdown()

async function renderSegment(index: number, text: string) {
  if (!text) {
    rendered.value[index] = ''
    return
  }
  try {
    rendered.value[index] = DOMPurify.sanitize(await process(text))
  }
  catch (error) {
    console.warn('Failed to process markdown with syntax highlighting, using fallback:', error)
    rendered.value[index] = DOMPurify.sanitize(processSync(text))
  }
}

watch(segments, (segs) => {
  segs.forEach((seg, index) => {
    if (seg.type === 'markdown')
      void renderSegment(index, seg.text)
  })
}, { immediate: true, deep: true })
</script>

<template>
  <div :class="props.class" class="markdown-content">
    <template v-for="(seg, index) in segments" :key="index">
      <MermaidBlock v-if="seg.type === 'mermaid'" :code="seg.code" />
      <!-- eslint-disable-next-line vue/no-v-html -->
      <div v-else v-html="rendered[index] || ''" />
    </template>
  </div>
</template>

<style scoped>
.markdown-content :deep(pre) {
  overflow-x: auto;
  max-width: 100%;
  border-radius: 6px;
  padding: 1rem;
  margin: 0.5rem 0;
}

.markdown-content :deep(code) {
  font-family: 'Fira Code', 'Monaco', 'Consolas', monospace;
  font-size: 0.875em;
}

.markdown-content :deep(pre code) {
  display: block;
  width: fit-content;
  min-width: 100%;
}

/* Ensure horizontal scrolling for wide code blocks */
.markdown-content :deep(pre.shiki) {
  overflow-x: auto;
  white-space: pre;
}

.markdown-content :deep(.shiki) {
  border-radius: 6px;
  padding: 1rem;
  margin: 0.5rem 0;
}

/* Fallback styles for non-shiki code blocks */
.markdown-content :deep(pre:not(.shiki)) {
  background: #f6f8fa;
  border: 1px solid #d0d7de;
}

.dark .markdown-content :deep(pre:not(.shiki)) {
  background: #161b22;
  border: 1px solid #30363d;
}

/* Force Shiki multi-theme output to follow our dark mode */
.dark .markdown-content :deep(.shiki) {
  background: var(--shiki-dark-bg, #0d1117) !important;
  color: var(--shiki-dark, #e6edf3) !important;
}

.dark .markdown-content :deep(.shiki span[style*="--shiki-dark"]) {
  color: var(--shiki-dark, inherit) !important;
}

.dark .markdown-content :deep(.shiki span[style*="--shiki-dark-background"]) {
  background-color: var(--shiki-dark-background, var(--shiki-dark-bg, transparent)) !important;
}
</style>
